import { Box } from '@mui/material';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchField } from '../../../components/inputs/SearchField';
import { useAuthContext } from '../../../contexts/AuthContext';
import { resizeImage } from '../../../utils/imageUtils';
import { useUpdateI18n } from '../../../hooks/api/useI18n';
import {
  I18nTabProps,
  getHashKeys,
  getI18nValue,
  updateI18nValue,
  getCategoryDisplayName,
} from '../utils/i18nHelpers';
import { IconsTabDesktop } from './IconsTabDesktop';
import { IconsTabMobile } from './IconsTabMobile';
import { env } from '../../../env';
import { AlertDialog } from '../../../components/AlertDialog';

export function IconsTab({
  currentLocale,
  i18nData,
  publicI18nData,
  onI18nDataChange,
  fieldErrors = {},
}: I18nTabProps) {
  const { t } = useTranslation();
  const { impersonationLock } = useAuthContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [editingDescription, setEditingDescription] = useState<string | null>(
    null
  );
  const [editingName, setEditingName] = useState<string | null>(null);
  const [isResetIconAlertOpen, setIsResetIconAlertOpen] = useState(false);
  const [iconToReset, setIconToReset] = useState<{
    category: string;
    key: string;
  } | null>(null);

  const getIconFieldError = useCallback(
    (category: string, iconKey: string) => {
      const localeErrors = fieldErrors[currentLocale];
      if (
        localeErrors &&
        typeof localeErrors === 'object' &&
        !Array.isArray(localeErrors)
      ) {
        const categoryErrors = (localeErrors as Record<string, unknown>)[
          category
        ];
        if (
          categoryErrors &&
          typeof categoryErrors === 'object' &&
          !Array.isArray(categoryErrors)
        ) {
          return (categoryErrors as Record<string, string[]>)[iconKey];
        }
      }
      return undefined;
    },
    [fieldErrors, currentLocale]
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sortBy, setSortBy] = useState<'name' | 'type'>('type');
  const [resettingItems, setResettingItems] = useState<Set<string>>(new Set());
  const locale = currentLocale || 'en';
  const updateI18nMutation = useUpdateI18n();

  // Unified icon update handler
  const updateIcon = useCallback(
    async (
      category: string,
      key: string,
      updates: Partial<{ icon: string; description: string; name: string }>,
      immediate = false
    ) => {
      // Only process updates for fields that exist in private data (are editable)
      const localeData = i18nData?.[locale];
      if (!localeData) return;

      const categoryData = localeData[category] as
        | Record<string, unknown>
        | undefined;
      const privateItem = categoryData?.[key] as
        | Record<string, unknown>
        | undefined;
      if (!privateItem && !immediate) return; // Skip if item doesn't exist in private data and not immediate

      const editableFields = {
        icon: privateItem?.icon !== undefined,
        description: privateItem?.description !== undefined,
        name: privateItem?.name !== undefined,
      };

      // Filter updates to only include editable fields
      const filteredUpdates: Record<string, unknown> = {};
      Object.entries(updates).forEach(([field, value]) => {
        if (editableFields[field as keyof typeof editableFields]) {
          filteredUpdates[field] = value;
        }
      });

      if (Object.keys(filteredUpdates).length === 0) return;

      const itemKey = `${category}-${key}`;

      try {
        if (immediate) {
          setResettingItems((prev) => new Set(prev).add(itemKey));
        }

        // Only include fields that are actually editable in the state
        const currentItem =
          (categoryData?.[key] as Record<string, unknown>) || {};
        const updatedItemData = { ...currentItem, ...filteredUpdates };

        if (immediate) {
          // Immediate API call for reset operations
          const apiData = {
            [locale]: {
              [category]: {
                [key]: updatedItemData,
              },
            },
          };

          updateI18nMutation.mutate(apiData, {
            onSuccess: (response) => {
              if (response && typeof response === 'object') {
                onI18nDataChange(response);
              }
            },
            onError: (error) => {
              console.error('Failed to update icon:', error);
            },
          });
        } else {
          // Local cache update for save-on-demand operations
          const updatedData = updateI18nValue(
            i18nData,
            locale,
            key,
            updatedItemData,
            category
          );
          onI18nDataChange(updatedData);
        }
      } catch (error) {
        console.error('Icon update failed:', error);
      } finally {
        if (immediate) {
          setResettingItems((prev) => {
            const newSet = new Set(prev);
            newSet.delete(itemKey);
            return newSet;
          });
        }
      }
    },
    [i18nData, locale, onI18nDataChange, updateI18nMutation]
  );

  const handleIconChange = useCallback(
    async (category: string, key: string, file: File) => {
      try {
        const resizedImage = await resizeImage(file, 'xs');
        await updateIcon(category, key, { icon: resizedImage }, false);
      } catch (error) {
        console.error('Failed to process image:', error);
      }
    },
    [updateIcon]
  );

  const handleDescriptionChange = useCallback(
    (category: string, key: string, description: string) => {
      const currentItem = getI18nValue(
        i18nData,
        locale,
        key,
        category,
        publicI18nData
      );
      if (currentItem.description !== description) {
        updateIcon(category, key, { description }, false);
      }
    },
    [updateIcon, i18nData, locale, publicI18nData]
  );

  const handleStartEditing = useCallback(
    (category: string, key: string) => {
      if (!impersonationLock) {
        setEditingDescription(`${category}-${key}`);
      }
    },
    [impersonationLock]
  );

  const handleFinishEditing = useCallback(
    (category: string, key: string, newDescription: string) => {
      handleDescriptionChange(category, key, newDescription);
      setEditingDescription(null);
    },
    [handleDescriptionChange]
  );

  const handleCancelEditing = useCallback(() => {
    setEditingDescription(null);
  }, []);

  const handleNameChange = useCallback(
    (category: string, key: string, name: string) => {
      updateIcon(category, key, { name }, false);
    },
    [updateIcon]
  );

  const handleStartEditingName = useCallback(
    (category: string, key: string) => {
      if (!impersonationLock) {
        setEditingName(`${category}-${key}`);
      }
    },
    [impersonationLock]
  );

  const handleFinishEditingName = useCallback(
    (category: string, key: string, newName: string) => {
      handleNameChange(category, key, newName);
      setEditingName(null);
    },
    [handleNameChange]
  );

  const handleCancelEditingName = useCallback(() => {
    setEditingName(null);
  }, []);

  const handleResetIcon = useCallback(
    async (category: string, key: string) => {
      await updateIcon(category, key, { icon: '' }, true);
    },
    [updateIcon]
  );

  const handleOpenResetDialog = useCallback(
    (category: string, key: string) => {
      setIconToReset({ category, key });
      setIsResetIconAlertOpen(true);
    },
    []
  );

  const iconItems = getHashKeys(locale, i18nData, publicI18nData);

  const filteredAndSortedItems = useMemo(() => {
    const items = iconItems.map(({ category, key, editableFields }) => {
      const currentItem = getI18nValue(
        i18nData,
        locale,
        key,
        category,
        publicI18nData
      );
      const type = getCategoryDisplayName(category);

      return {
        name: currentItem.name || key,
        key,
        category,
        type,
        editableFields,
        current: {
          icon: currentItem.icon || '',
          description: currentItem.description || '',
          name: currentItem.name,
          url: currentItem.url || '',
        },
      };
    });

    const filtered = items.filter(
      (item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.current.description
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );

    return filtered.sort((a, b) => {
      if (sortBy === 'type') {
        const typeComparison =
          sortOrder === 'asc'
            ? a.type.localeCompare(b.type)
            : b.type.localeCompare(a.type);
        if (typeComparison === 0) {
          return a.name.localeCompare(b.name);
        }
        return typeComparison;
      } else {
        const typeComparison = a.type.localeCompare(b.type);
        if (typeComparison === 0) {
          return sortOrder === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        }
        return typeComparison;
      }
    });
  }, [
    iconItems,
    i18nData,
    publicI18nData,
    locale,
    searchTerm,
    sortBy,
    sortOrder,
  ]);

  const handleSort = (column: 'name' | 'type') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const BASE_ASSET_URL = env.PUBLIC_SEARCH_URL;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          mt: 22,
          width: '100%',
        }}
      >
        <SearchField
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('branding.iconsTab.searchPlaceholder') as string}
          ariaLabel={t('branding.iconsTab.searchPlaceholder') as string}
        />
      </Box>

      <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 24 }}>
        <IconsTabDesktop
          filteredAndSortedItems={filteredAndSortedItems}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          editingDescription={editingDescription}
          editingName={editingName}
          onStartEditingName={handleStartEditingName}
          onFinishEditingName={handleFinishEditingName}
          onCancelEditingName={handleCancelEditingName}
          impersonationLock={impersonationLock}
          resettingItems={resettingItems}
          onStartEditing={handleStartEditing}
          onFinishEditing={handleFinishEditing}
          onCancelEditing={handleCancelEditing}
          onIconChange={handleIconChange}
          onResetIcon={handleOpenResetDialog}
          getIconFieldError={getIconFieldError}
          baseAssetUrl={BASE_ASSET_URL}
        />
      </Box>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <IconsTabMobile
          filteredAndSortedItems={filteredAndSortedItems}
          editingDescription={editingDescription}
          editingName={editingName}
          onStartEditingName={handleStartEditingName}
          onFinishEditingName={handleFinishEditingName}
          onCancelEditingName={handleCancelEditingName}
          impersonationLock={impersonationLock}
          resettingItems={resettingItems}
          onStartEditing={handleStartEditing}
          onFinishEditing={handleFinishEditing}
          onCancelEditing={handleCancelEditing}
          onIconChange={handleIconChange}
          onResetIcon={handleOpenResetDialog}
          getIconFieldError={getIconFieldError}
          baseAssetUrl={BASE_ASSET_URL}
        />
      </Box>
      <AlertDialog
        variant="alert"
        open={isResetIconAlertOpen}
        onClose={() => setIsResetIconAlertOpen(false)}
        title={t('branding.iconsTab.resetIconAlertTitle')}
        description={t('branding.iconsTab.resetIconAlertDescription')}
        actions={{
          cancel: {
            label: t('branding.iconsTab.resetIconAlertCancel'),
            onClick: () => setIsResetIconAlertOpen(false),
          },
          confirm: {
            label: t('branding.iconsTab.resetIconAlertConfirm'),
            onClick: () => {
              if (iconToReset) {
                handleResetIcon(iconToReset.category, iconToReset.key);
              }
              setIsResetIconAlertOpen(false);
            },
          },
        }}
      />
    </Box>
  );
}
